/* ============================================================
   melusio-buy-box.js — shared option-selector + add-to-bag wiring.

   One engine for the quick-view overlay AND the PDP buy box. A product renders one
   labelled group per option (ds-size-selector.liquid), and this file resolves the chosen
   combination against the server's variant map: with two options the variant is the
   intersection of two picks, so no single pill can carry a variant id.

   Money is never formatted here. Every price arrives pre-formatted by Liquid inside the
   variant map, and the CTA label is string concatenation only.

   window.MelusioBuyBox.wire(root, opts) — root contains [data-qv-sizes] groups,
   [data-qv-variants] and [data-qv-add].
     opts.urlSync  : true → on selection, history.replaceState ?variant=<id> (matches
                     Horizon's variant-picker contract: replace, never a new history entry).
     opts.onSelect : called with (variant, pill) after each selection, where variant is the
                     resolved map entry or null (the PDP syncs the Buy-it-now form's hidden
                     variant id from it).
     opts.onAdded  : called after a successful add (quick-view uses it to close itself).
   Returns { select(pill) } so callers can preselect programmatically.

   ONE SIZE ROW (#74). When ds-size-selector renders a joint row ([data-qv-joint]), a tap
   on it presses the matching pill in EVERY option group through select(), so the groups
   stay the single source of the selection and nothing below them changes. The row only
   mirrors: it lights the value all groups share and disables a value whose all-same
   combination cannot be bought. "Need different sizes?" and "Same size for both" swap the
   two views. Once split it stays split, even when the picks match again.
   ============================================================ */
(function () {
  function wire(root, opts) {
    opts = opts || {};
    const groups = Array.from(root.querySelectorAll('[data-qv-sizes]'));
    const cta = root.querySelector('[data-qv-add]');
    const ctaLabel = root.querySelector('[data-qv-cta-label]');
    const errEl = root.querySelector('[data-qv-error]');

    // Server-rendered map: [{id, options:[...], available, price_money}]. A malformed or
    // missing map leaves an empty list, which resolves nothing and keeps the CTA in the
    // state the server rendered it — the same failure posture as a missing cart engine.
    let variants = [];
    const mapEl = root.querySelector('[data-qv-variants]');
    if (mapEl) {
      try { variants = JSON.parse(mapEl.textContent) || []; } catch (e) { variants = []; }
    }

    const positionOf = (group) => Number(group.getAttribute('data-option-position'));
    const pillsIn = (group) => Array.from(group.querySelectorAll('[data-qv-size]'));

    // What each group currently holds, keyed by 1-based option position. Read back out of
    // the markup so the server's render is the starting state, with no parallel copy of it.
    function selection() {
      const sel = {};
      groups.forEach((group) => {
        const pill = group.querySelector('[data-qv-size].is-selected');
        if (pill) sel[positionOf(group)] = pill.getAttribute('data-option-value');
      });
      return sel;
    }

    // The variant matching every group's pick. null while any group is unpicked.
    function resolve(sel) {
      const positions = groups.map(positionOf);
      if (positions.some((p) => sel[p] == null)) return null;
      return variants.find((v) => positions.every((p) => v.options[p - 1] === sel[p])) || null;
    }

    // Recompute which values are still reachable and disable the rest — disabled, never
    // hidden, so the customer can see the size exists and is simply not pairable.
    // Cross-option filtering runs only from a combination that exists; when the current
    // pair is itself unavailable (a ?variant= deep link to a sold-out combination) every
    // group falls back to "available anywhere", so no group empties and traps the customer.
    // ds-size-selector.liquid renders the identical rule, so this pass never flips the
    // first paint.
    function refreshAvailability() {
      const sel = selection();
      const current = resolve(sel);
      const usePairs = !!(current && current.available);
      groups.forEach((group) => {
        const position = positionOf(group);
        pillsIn(group).forEach((pill) => {
          const value = pill.getAttribute('data-option-value');
          const reachable = variants.some((v) => {
            if (!v.available || v.options[position - 1] !== value) return false;
            if (!usePairs) return true;
            return groups.every((other) => {
              const q = positionOf(other);
              return q === position || v.options[q - 1] === sel[q];
            });
          });
          pill.disabled = !reachable;
          if (reachable) pill.removeAttribute('aria-disabled');
          else pill.setAttribute('aria-disabled', 'true');
        });
      });
      return current;
    }

    // The CTA mirrors the resolved variant: addable with its price, sold out when the pair
    // exists but cannot be bought, or back to the server's select-a-size copy when a group
    // has no pick. Each label comes from a data-* attribute Liquid filled from theme settings.
    function applyToCta(variant) {
      if (!cta) return;
      if (variant && variant.available) {
        cta.disabled = false;
        cta.setAttribute('data-variant-id', String(variant.id));
        if (ctaLabel) ctaLabel.textContent = (cta.getAttribute('data-add-label') || '') + ' · ' + variant.price_money;
        return;
      }
      cta.disabled = true;
      cta.removeAttribute('data-variant-id');
      const fallback = cta.getAttribute(variant ? 'data-sold-out-label' : 'data-select-label');
      if (ctaLabel && fallback) ctaLabel.textContent = fallback;
    }

    function select(pill) {
      if (!pill) return null;
      const group = pill.closest('[data-qv-sizes]');
      if (!group) return null;
      // Only this group's pills change; the other group keeps whatever it holds.
      pillsIn(group).forEach((p) => { p.classList.remove('is-selected'); p.setAttribute('aria-pressed', 'false'); });
      pill.classList.add('is-selected');
      pill.setAttribute('aria-pressed', 'true');
      const variant = refreshAvailability();
      applyToCta(variant);
      refreshJoint();
      if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
      return variant;
    }

    // What a pick tells the page: the ?variant= URL and the caller's hook. Shared by the
    // per-option pills and the joint row so both report the same way.
    function announce(variant, pill) {
      if (opts.urlSync && variant) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }
      if (opts.onSelect) opts.onSelect(variant, pill);
    }

    // ---- one size row (#74) ----
    const joint = root.querySelector('[data-qv-joint]');
    const splitGroups = root.querySelector('[data-qv-split-groups]');
    const jointPills = joint ? Array.from(joint.querySelectorAll('[data-qv-joint-size]')) : [];

    function refreshJoint() {
      if (!joint) return;
      const sel = selection();
      const picks = groups.map((g) => sel[positionOf(g)]);
      const shared = picks.every((v) => v != null && v === picks[0]) ? picks[0] : null;
      jointPills.forEach((p) => {
        const value = p.getAttribute('data-option-value');
        const on = value === shared;
        p.classList.toggle('is-selected', on);
        p.setAttribute('aria-pressed', on ? 'true' : 'false');
        const ok = variants.some((v) => v.available && v.options.every((o) => o === value));
        p.disabled = !ok;
        if (ok) p.removeAttribute('aria-disabled');
        else p.setAttribute('aria-disabled', 'true');
      });
    }

    function pillFor(group, value) {
      return pillsIn(group).find((p) => p.getAttribute('data-option-value') === value) || null;
    }

    // Press `value` in every group. Returns the resolved variant.
    function selectAll(value) {
      let variant = null;
      groups.forEach((group) => { const p = pillFor(group, value); if (p) variant = select(p); });
      return variant;
    }

    function setMode(split, focusTarget) {
      if (!joint || !splitGroups) return;
      joint.hidden = split;
      splitGroups.hidden = !split;
      if (focusTarget) focusTarget.focus();
    }

    if (joint && splitGroups) {
      joint.addEventListener('click', (e) => {
        const pill = e.target.closest('[data-qv-joint-size]');
        if (pill && !pill.disabled) announce(selectAll(pill.getAttribute('data-option-value')), pill);
      });
      joint.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const pills = jointPills.filter((p) => !p.disabled);
        const i = pills.indexOf(document.activeElement);
        if (i < 0) return;
        e.preventDefault();
        pills[(i + (e.key === 'ArrowRight' ? 1 : -1) + pills.length) % pills.length].focus();
      });

      const splitBtn = joint.querySelector('[data-qv-split]');
      const joinBtn = splitGroups.querySelector('[data-qv-join]');
      // Focus lands on the counterpart link, which sits where the tapped one was, so a
      // keyboard or screen-reader user is not dropped back at the top of the buy box.
      if (splitBtn) splitBtn.addEventListener('click', () => setMode(true, joinBtn));
      if (joinBtn) joinBtn.addEventListener('click', () => {
        // Fold back to the FIRST group's size (the hoodie). When that same-size pair can
        // be bought, every group takes it; otherwise nothing stays picked and the CTA
        // returns to "Select a size", rather than keeping a mixed pair the one row
        // cannot show.
        const first = selection()[positionOf(groups[0])];
        const ok = first != null && variants.some((v) => v.available && v.options.every((o) => o === first));
        if (ok) {
          announce(selectAll(first), joinBtn);
        } else {
          groups.forEach((g) => pillsIn(g).forEach((p) => { p.classList.remove('is-selected'); p.setAttribute('aria-pressed', 'false'); }));
          applyToCta(refreshAvailability());
          refreshJoint();
          if (opts.urlSync) {
            const url = new URL(location.href);
            url.searchParams.delete('variant');
            history.replaceState({}, '', url.toString());
          }
          if (opts.onSelect) opts.onSelect(null, joinBtn);
        }
        setMode(false, splitBtn);
      });
    }

    groups.forEach((group) => {
      group.addEventListener('click', (e) => {
        const pill = e.target.closest('[data-qv-size]');
        if (!pill || pill.disabled) return;
        announce(select(pill), pill);
      });

      // Arrow keys move focus along a group, skipping values that cannot be picked.
      // tabindex is deliberately untouched: these are aria-pressed buttons inside a
      // role=group, so every pill belongs in the tab order. A roving tabindex is the
      // radiogroup pattern, and turning these into radios would rewrite the single-option
      // markup that the hoodie and the sweatpant share and this change must leave alone.
      group.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const pills = pillsIn(group).filter((p) => !p.disabled);
        const i = pills.indexOf(document.activeElement);
        if (i < 0) return;
        e.preventDefault();
        pills[(i + (e.key === 'ArrowRight' ? 1 : -1) + pills.length) % pills.length].focus();
      });
    });

    if (cta) cta.addEventListener('click', async () => {
      if (cta.disabled) return;
      const variantId = cta.getAttribute('data-variant-id');
      if (!variantId) return;
      const mc = window.MelusioCart;
      if (!mc || !mc.addItem) return; // JS cart unavailable — leave the buy box as-is
      const prev = ctaLabel ? ctaLabel.textContent : '';
      cta.disabled = true;
      if (ctaLabel) ctaLabel.textContent = cta.getAttribute('data-adding-label') || '';
      // Clear a previous refusal as the retry starts — the same clear select() does on a
      // new pick. Here rather than in the success branch below, which stays untouched: an
      // add that works must not leave "Could not add to bag." standing next to the drawer
      // it just opened, and clearing at the START also covers the second failure, where
      // the message is rewritten rather than newly shown.
      if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
      const result = await mc.addItem(variantId, 1);
      // Restore on the ADD RESPONSE, success or failure — never the drawer lifecycle
      // (the drawer emits nothing on close, so a drawer-coupled restore would strand
      // "Adding…"). Re-derive from the CURRENT selection instead of blindly re-enabling:
      // applyToCta rebuilds `Add to bag · {price}` from the resolved variant and enables
      // only for one that is addable, so a buy box whose selection somehow vanished falls
      // back to the disabled select-a-size state. Sold-out never reaches here (its CTA is
      // unclickable) and the hydration gate below is independent of this.
      if (groups.length) {
        applyToCta(refreshAvailability());
      } else {
        cta.disabled = false;
        if (ctaLabel) ctaLabel.textContent = prev;
      }
      if (result && result.ok) {
        if (opts.onAdded) opts.onAdded();
      } else if (errEl) {
        errEl.textContent = (result && result.error) || cta.getAttribute('data-add-error') || '';
        errEl.hidden = false;
      }
    });

    // First availability pass, after the listeners exist. It deliberately does NOT touch
    // the CTA: the server owns the CTA's initial label and disabled state, and the
    // hydration gate below stays the only thing that can enable it.
    if (groups.length) { refreshAvailability(); refreshJoint(); }

    // Hydration gate (FINAL step — listeners above are attached): the CTA is server-rendered
    // disabled so it can't swallow clicks before this wiring exists. Enable ONLY the CTA the
    // server marked data-hydrate-enable (variant resolved + available); sold-out and
    // select-a-size render without the marker and stay disabled until a pill is selected.
    if (cta && cta.hasAttribute('data-hydrate-enable')) {
      cta.removeAttribute('data-hydrate-enable');
      cta.disabled = false;
    }

    return { select };
  }

  window.MelusioBuyBox = { wire };
})();
